import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'Bug', label: 'Bug' },
  { value: 'Feature Request', label: 'Feature Request' },
  { value: 'Billing Issue', label: 'Billing Issue' },
  { value: 'OCR Failure', label: 'OCR Failure' },
  { value: 'Data Error', label: 'Data Error' },
  { value: 'Other', label: 'Other' }
];

const PRIORITIES = [
  { value: 'Low', label: 'Low' },
  { value: 'Normal', label: 'Normal' },
  { value: 'High', label: 'High' }
];

const CreateTicketModal = ({ isOpen, onClose, onTicketCreated }) => {
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'Normal',
    description: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const data = new FormData();
      data.append('subject', formData.subject);
      data.append('category', formData.category);
      data.append('priority', formData.priority);
      data.append('description', formData.description);
      if (attachment) {
        data.append('attachment', attachment);
      }

      const response = await axios.post(`${API}/support-tickets`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Check if email was sent
      if (response.data.email_sent === false) {
        toast.warning('Ticket submitted, but email delivery failed — please retry sending email.', {
          duration: 6000
        });
      } else {
        toast.success('Ticket submitted successfully');
      }

      onTicketCreated(response.data.ticket);
      resetForm();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      category: '',
      priority: 'Normal',
      description: ''
    });
    setAttachment(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachment(file);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: '#FFFFFF',
          color: '#000000',
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          opacity: 1
        }}
      >
        <DialogHeader>
          <DialogTitle 
            className="text-2xl font-bold" 
            style={{ color: '#245451', opacity: 1 }}
          >
            Create New Support Ticket
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Subject */}
          <div>
            <Label 
              htmlFor="subject" 
              className="text-base font-semibold" 
              style={{ color: '#000000', opacity: 1 }}
            >
              Subject <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Brief summary of your issue"
              className="mt-2"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#000000',
                borderColor: '#CED4DA',
                opacity: 1
              }}
              required
            />
          </div>

          {/* Category */}
          <div>
            <Label 
              htmlFor="category" 
              className="text-base font-semibold" 
              style={{ color: '#000000', opacity: 1 }}
            >
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger 
                className="mt-2"
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  borderColor: '#CED4DA',
                  opacity: 1
                }}
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  border: '1px solid #CED4DA',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  opacity: 1,
                  zIndex: 9999
                }}
              >
                {CATEGORIES.map((cat) => (
                  <SelectItem 
                    key={cat.value} 
                    value={cat.value}
                    style={{
                      backgroundColor: '#FFFFFF',
                      color: '#000000',
                      opacity: 1
                    }}
                  >
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div>
            <Label 
              htmlFor="priority" 
              className="text-base font-semibold" 
              style={{ color: '#000000', opacity: 1 }}
            >
              Priority
            </Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => setFormData({ ...formData, priority: value })}
            >
              <SelectTrigger 
                className="mt-2"
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  borderColor: '#CED4DA',
                  opacity: 1
                }}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  border: '1px solid #CED4DA',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  opacity: 1,
                  zIndex: 9999
                }}
              >
                {PRIORITIES.map((pri) => (
                  <SelectItem 
                    key={pri.value} 
                    value={pri.value}
                    style={{
                      backgroundColor: '#FFFFFF',
                      color: '#000000',
                      opacity: 1
                    }}
                  >
                    {pri.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label 
              htmlFor="description" 
              className="text-base font-semibold" 
              style={{ color: '#000000', opacity: 1 }}
            >
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your issue in detail..."
              className="mt-2 min-h-[150px]"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#000000',
                borderColor: '#CED4DA',
                opacity: 1
              }}
              required
            />
          </div>

          {/* Attachment */}
          <div>
            <Label 
              htmlFor="attachment" 
              className="text-base font-semibold" 
              style={{ color: '#000000', opacity: 1 }}
            >
              Attachment (Optional)
            </Label>
            <div className="mt-2">
              {attachment ? (
                <div 
                  className="flex items-center justify-between p-3 border rounded-lg"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#CED4DA',
                    opacity: 1
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Upload className="h-5 w-5" style={{ color: '#245451', opacity: 1 }} />
                    <span className="text-sm" style={{ color: '#000000', opacity: 1 }}>
                      {attachment.name} ({(attachment.size / 1024).toFixed(2)} KB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAttachment(null)}
                    style={{ opacity: 1 }}
                  >
                    <X className="h-4 w-4" style={{ color: '#000000' }} />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#CED4DA',
                    opacity: 1
                  }}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: '#94A3B8', opacity: 1 }} />
                  <Label
                    htmlFor="file-upload"
                    className="cursor-pointer text-sm"
                    style={{ color: '#000000', opacity: 1 }}
                  >
                    Click to upload or drag and drop
                    <br />
                    <span className="text-xs">Max file size: 10MB</span>
                  </Label>
                  <Input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              className="flex-1"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#000000',
                borderColor: '#CED4DA',
                opacity: 1
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1"
              style={{ 
                backgroundColor: '#D4AF37', 
                color: '#FFFFFF',
                opacity: 1,
                border: 'none'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTicketModal;
